import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzContentControlDataProtectionRulesGridDataProvider } from './GzContentControlDataProtectionRulesGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { ContentControlDataProtectionRule } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzContentControlWebDataProtectionRulesFormWebComponent } from './rulesItemForm';
import { validateNameDuplicates, validateStringDuplicates } from './utils/validators';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_RULES_GRID)
export class GzContentControlDataProtectionRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlDataProtectionRule,
  ContentControlDataProtectionRule,
  GzContentControlDataProtectionRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(this, this.onRuleNameClick.bind(this)),
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;
  protected hasBasicEditRowAction = true;
  protected hasEditRowAction = false;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  private currentBodyForm: GzContentControlWebDataProtectionRulesFormWebComponent;

  public set items(value: ContentControlDataProtectionRule[]) {
    super.setFormValue(value);
  }

  protected getDataProvider(): GzContentControlDataProtectionRulesGridDataProvider {
    return new GzContentControlDataProtectionRulesGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete rules?'),
          translateFn('You are about to delete the selected rules. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  private onAddButtonClick(): void {
    const addRuleCallback = (formValue: ContentControlDataProtectionRule): ValidationResult =>
      this.onNewGridItemAdd(formValue);

    // create configs
    this.currentBodyForm = new GzContentControlWebDataProtectionRulesFormWebComponent();
    const configs: FullPagePopupConfigs = {
      bodyForm: this.currentBodyForm,
      strings: {
        title: translateFn('Add data protection rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.onRuleNameClick(event.detail.rowData.item);
  }

  private onRuleNameClick(item: ContentControlDataProtectionRule): void {
    const addRuleCallback = (formValue: ContentControlDataProtectionRule): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    this.currentBodyForm = new GzContentControlWebDataProtectionRulesFormWebComponent(item);
    const configs: FullPagePopupConfigs = {
      bodyForm: this.currentBodyForm,
      strings: {
        title: translateFn('Edit data protection rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ContentControlDataProtectionRule): ValidationResult {
    let isValid = true;

    // check for duplicates
    const nameDuplicatesValidation = validateNameDuplicates(gridItemFormValue, this.getFormValue());
    if (!nameDuplicatesValidation.valid) {
      this.currentBodyForm.setItemNameError(nameDuplicatesValidation.error);
      isValid = false;
    }

    // check for duplicates
    const stringDuplicatesValidation = validateStringDuplicates(gridItemFormValue, this.getFormValue());
    if (!stringDuplicatesValidation.valid) {
      this.currentBodyForm.setItemStringError(stringDuplicatesValidation.error);
      isValid = false;
    }

    return isValid ? { valid: true } : { valid: false, error: '' };
  }

  protected validateGridItemBeforeEdit(
    oldItem: ContentControlDataProtectionRule,
    newItem: ContentControlDataProtectionRule
  ): ValidationResult {
    // skip if item is not changed
    const hasSameName = oldItem.name === newItem.name;
    const hasSameString = oldItem.type === newItem.type && oldItem.string === newItem.string;
    if (hasSameName && hasSameString) {
      return { valid: true };
    }

    let isValid = true;

    // check for duplicates if name is changed
    const nameDuplicatesValidation: ValidationResult = hasSameName
      ? { valid: true }
      : validateNameDuplicates(newItem, this.getFormValue());
    if (!nameDuplicatesValidation.valid) {
      this.currentBodyForm.setItemNameError(nameDuplicatesValidation.error);
      isValid = false;
    }

    // check for duplicates
    const stringDuplicatesValidation: ValidationResult = hasSameString
      ? { valid: true }
      : validateStringDuplicates(newItem, this.getFormValue());
    if (!stringDuplicatesValidation.valid) {
      this.currentBodyForm.setItemStringError(stringDuplicatesValidation.error);
      isValid = false;
    }

    return isValid ? { valid: true } : { valid: false, error: '' };
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
  }
}
